import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { IntegratedFtpCreateContainer } from '../models/integrated-ftp-create-container';
import { FtpCreateResult } from '../models/ftp-create-result';
import { IntegratedFtpValueContainer } from '../models/integrated-ftp-value-container';
import { ConfigurationService } from '../../configuration/services/configuration.service';
import { FtpDirectoryListing } from '../models/ftp-directory-listing';

@Injectable({
    providedIn: 'root'
})
export class PartnerFtpService {

    constructor(private httpService: JsonHttpService, private configService: ConfigurationService) { }

    createFtp(createContainer: IntegratedFtpCreateContainer): Promise<FtpCreateResult> {
        return this.httpService.post('/api/partners/integrated-ftp/create-ftp', createContainer).toPromise();
    };

    updatePublicKey(partnerId: number, ftpUserName: string, fileIdentifier: string): Promise<boolean> {
        const options = {
            params: new HttpParams()
                .set('partnerId', `${partnerId}`)
                .set('ftpUserName', ftpUserName)
                .set('fileIdentifier', fileIdentifier)
        };
        return this.httpService.post('/api/partners/integrated-ftp/update-public-key', null, options).toPromise();
    };

    deleteFtp(partnerId: number): Promise<void> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        return this.httpService.post('/api/partners/integrated-ftp/delete-ftp', null, options).toPromise();
    };

    changePassword(valueContainer: IntegratedFtpValueContainer): Promise<void> {
        return this.httpService.post('/api/partners/integrated-ftp/change-ftp-password', valueContainer).toPromise();
    }

    changeUsername(valueContainer: IntegratedFtpValueContainer): Promise<void> {
        return this.httpService.post('/api/partners/integrated-ftp/change-ftp-username', valueContainer).toPromise();
    }

    setIntegratedFtp(partnerId: number, includeInIntegratedFtpRetrieve: boolean): Promise<void> {
        const options = {
            params: new HttpParams()
                .set('partnerId', `${partnerId}`)
                .set('includeInIntegratedFtpRetrieve', `${!!includeInIntegratedFtpRetrieve}`)
        };
        return this.httpService.post('/api/partners/integrated-ftp/set-integrated-ftp', null, options).toPromise();
    }

    usernameExists(username: string): Promise<boolean> {
        const options = { params: new HttpParams().set('username', `${username}`) };
        return this.httpService.get('/api/partners/integrated-ftp/username-exists', options).toPromise();
    }

    generateUsername(): Promise<string> {
        return this.httpService.get('/api/partners/integrated-ftp/generate-username').toPromise();
    }

    getInitialSubDirectories(): Promise<string[]> {
        return this.httpService.get('/api/partners/integrated-ftp/get-initial-sub-directories').toPromise();
    }

    getPluginName(): Promise<string> {
        return this.httpService.get('/api/partners/integrated-ftp/get-plugin-name').toPromise();
    }

    /**
     * Gets the ftp-password for the given partner. This call requires the ShowPasswords permission - otherwise an unauthorized exception will be thrown from the backend.
     * @param partnerId
     */
    getFtpPassword(partnerId: number): Promise<string> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        return this.httpService.get('/api/partners/integrated-ftp/get-ftp-password', options).toPromise();
    }

    /**
     * Gets the information about the ftp-directory for the given partner.
     * @param partnerId
     */
    getDirectoriesAndFiles(partnerId: number): Promise<FtpDirectoryListing> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        return this.httpService.get('/api/partners/integrated-ftp/get-directories-and-files', options).toPromise();
    }

    /**
     * Gets the information about the ftp-directory public key feature suppored by the provider.
     */
    supportPublicKey(): Promise<boolean> {
        return this.httpService.get('/api/partners/integrated-ftp/support-public-key', null, false).toPromise();
    }

    supportChangePassword(): Promise<boolean> {
        return this.httpService.get('/api/partners/integrated-ftp/support-change-password', null, false).toPromise();
    }
    /**
     * Gets the information about the any ftp provider configured in the config.
     */
    hasAnyFTPProvider(): Promise<boolean> {
        return this.httpService.get('/api/partners/integrated-ftp/has-any-provider', null).toPromise();
    }

    async canChangeUsername(): Promise<boolean> {
        const partnerFtpChangeUserName = await this.configService.getEffectiveSettingValue('PartnerFtp_ChangeUsername');
        if (partnerFtpChangeUserName && partnerFtpChangeUserName.toLowerCase() === 'true') {
            return true;
        }
        return false;
    }
}
